/**
 * Ticker tape for the landing page hero.
 *
 * Live quotes now sit behind auth (GV-02: spot / prev-close / %-change
 * data is paywalled), so this unauthenticated tape no longer polls
 * /api/quotes/marquee — doing so would 401 in a retry loop and blank the
 * row. Instead it renders a STATIC sample tape (representative figures,
 * clearly labelled "SAMPLE · SIGN IN FOR LIVE") so the hero keeps its
 * Bloomberg-tape character without making a false "live" claim. The real,
 * streaming tape is on the authenticated /app/* pages.
 *
 * The 1Hz ET clock is kept — it's local (no network) and reinforces the
 * market-terminal feel.
 */

import React, { useEffect, useState } from 'react';

interface MarqueeItem {
  symbol: string;
  last: number;
  pct_change: number;
}

// Representative, plausible-but-static figures. Intentionally NOT fetched
// — they illustrate the tape a signed-in user gets. Fixed left-to-right
// order matches the former live tape (SPX · NDX · SPY · QQQ · IWM · VIX).
const SAMPLE_ITEMS: MarqueeItem[] = [
  { symbol: 'SPX', last: 5832.42, pct_change: 0.34 },
  { symbol: 'NDX', last: 20415.10, pct_change: 0.52 },
  { symbol: 'SPY', last: 581.20, pct_change: 0.31 },
  { symbol: 'QQQ', last: 497.85, pct_change: 0.49 },
  { symbol: 'IWM', last: 224.60, pct_change: -0.18 },
  { symbol: 'VIX', last: 13.95, pct_change: -1.42 },
];

function clockET(): string {
  // Render exchange-time HH:MM:SS in ET. Format isn't critical so use
  // toLocaleTimeString with America/New_York; falls back to local time
  // when ICU isn't available (older browsers / private modes).
  try {
    return new Date().toLocaleTimeString('en-US', {
      timeZone: 'America/New_York',
      hour12: false,
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return new Date().toLocaleTimeString();
  }
}

const MarqueeTape: React.FC = () => {
  // 1Hz clock only — no network. Re-render once a second to tick the time.
  const [, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="gv-marquee" role="region" aria-label="Sample market quotes — sign in for live data">
      <div className="gv-marquee-row">
        {SAMPLE_ITEMS.map(it => {
          const dir = it.pct_change >= 0 ? 'up' : 'down';
          const sign = it.pct_change >= 0 ? '+' : '';
          return (
            <div key={it.symbol} className="gv-marq-item">
              <span className="gv-marq-sym">{it.symbol}</span>
              <span className="gv-marq-px">
                {it.last.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span className={`gv-marq-chg gv-marq-chg--${dir}`}>
                {`${sign}${it.pct_change.toFixed(2)}%`}
              </span>
            </div>
          );
        })}
        <div className="gv-marq-status">
          {/* Static (non-pulsing) dot + honest framing: these are sample
              figures and live data is behind sign-in. */}
          <span className="gv-marq-dot is-stale" aria-hidden="true" />
          SAMPLE · SIGN IN FOR LIVE · {clockET()} ET
        </div>
      </div>
    </div>
  );
};

export default MarqueeTape;
